<template>
  <bm-control v-bind="$attrs" :read-only-value="formattedDate">
    <template #default="{ dataQa }">
      <v-menu
        v-model="menu"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template #activator="{ on }">
          <v-text-field
            :value="formattedDate"
            class="bm-text"
            :placeholder="placeholderVal"
            :prepend-icon="bmIconName('Event')"
            readonly
            v-bind="$attrs"
            :clearable="clearable"
            color="brand02"
            :label="undefined"
            :data-qa="dataQa"
            v-on="on"
            @input="textFieldChanged"
          >
          </v-text-field>
        </template>
        <v-date-picker
          v-model="date"
          no-title
          scrollable
          color="brand02"
          :events="events"
          :data-qa="`${dataQa}_picker`"
          :first-day-of-week="startDayOfWeek"
          @input="menu = false"
        >
        </v-date-picker>
      </v-menu>
    </template>
  </bm-control>
</template>

<script lang="ts">
export default {
  name: "BmDateQ",
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import { LocalDate } from "@js-joda/core";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { computed, ref, watch } from "vue";
import userStore from "@/store/modules/userStore";
import { bmIconName } from "@/components/controls/bmIconMap";

interface BmDateQProps {
  value?: LocalDate | null;
  placeholder?: string;
  clearable?: boolean;
  dateEvents?: (date: LocalDate) => string;
}

interface BmDateQEvents {
  (event: "update:value", value: LocalDate | null): void;
}

const props = withDefaults(defineProps<BmDateQProps>(), {
  value: null,
});

const emit = defineEmits<BmDateQEvents>();

const menu = ref(false);
const date = ref<string | null>("");

const placeholderVal = computed(() => {
  if (!props.placeholder) {
    return " ";
  }

  return props.placeholder;
});

const formattedDate = computed(() => {
  if (!date.value) {
    return "";
  }

  // Use the tenants date formatter to show the ISO 8601 date in a preferable format.
  return tenantService.formatDate(LocalDate.parse(date.value));
});

const startDayOfWeek = computed(
  () => userStore.state.user?.calendarStartDay.key ?? 1
);

watch(
  () => props.value,
  (val) => {
    date.value = val ? val.toString() : null;
  },
  { immediate: true }
);

watch(date, (val) => {
  if (val == null) {
    emit("update:value", null);
  } else {
    emit("update:value", LocalDate.parse(val));
  }
});

const textFieldChanged = (val: string | null) => {
  // ensure we clear the date if the value is cleared via the textbox
  if (val === null) {
    date.value = null;
  }
};

const events = (dateStr: string) =>
  props.dateEvents && dateStr ? props.dateEvents(LocalDate.parse(dateStr)) : "";
</script>

<style scoped lang="sass">
@import "src/sass/typography"

.bm-text
  @include bm-field
</style>
